import i18n from '@/i18n';
import type { GuildMemberRow } from '@/types/api';

/**
 * Pure view helpers for the voice room, extracted from VoiceChannelStub so the
 * stream-tag bucketing, live-track detection, capacity copy, and participant
 * naming can be unit-tested without rendering the component or wiring the
 * voiceMedia store. No React, no DOM beyond the MediaStream track API.
 */

/** Minimal shape of a remote media stream (the store's RemoteVoiceStream).
 *  Kept structural so we don't import a non-exported lib type. */
export interface TaggedStream {
  userId: string;
  stream: MediaStream;
  mediaTag: string;
}

/** Map userId → stream for one media tag (`camera` | `screen` | …). */
export function streamsByTag(
  remoteStreams: TaggedStream[],
  tag: string,
): Map<string, MediaStream> {
  return new Map(
    remoteStreams.filter((remote) => remote.mediaTag === tag).map((remote) => [remote.userId, remote.stream]),
  );
}

/**
 * True when the stream carries at least one *live* video track. A stream whose
 * tracks have all ended (camera off / screen-share stopped) can linger as an
 * object, so presence of the stream alone isn't enough to render a tile.
 */
export function hasLiveVideoTrack(stream: MediaStream | null | undefined): boolean {
  return stream?.getVideoTracks().some((track) => track.readyState === 'live') ?? false;
}

/** True when the stream carries at least one *live* audio track. */
export function hasLiveAudioTrack(stream: MediaStream | null | undefined): boolean {
  return stream?.getAudioTracks().some((track) => track.readyState === 'live') ?? false;
}

/**
 * Who is sharing a screen that CARRIES SOUND.
 *
 * A silent share and a share with system audio look identical on a tile today,
 * and «в демонстрации нет звука» has already been reported as a bug when it was
 * simply a silent share. The sender publishes sound as its own `screen-audio`
 * producer, so presence of a live audio track under that tag is the answer.
 */
export function screenAudioUserIds(remoteStreams: TaggedStream[]): Set<string> {
  const ids = new Set<string>();
  for (const remote of remoteStreams) {
    if (remote.mediaTag === 'screen-audio' && hasLiveAudioTrack(remote.stream)) ids.add(remote.userId);
  }
  return ids;
}

/**
 * Capacity subheader line, three states:
 *  - limit set:        "3 of 5 members" (ratio reads at a glance)
 *  - no limit, people: "3 members"
 *  - no limit, empty:  "empty — be the first to join" (so "0" doesn't read as
 *    a hard cap of zero)
 */
export function capacityLabel(count: number, userLimit: number | null): string {
  if (userLimit && userLimit > 0) return i18n.t('lib2.voiceRoom.capacity', { count, limit: userLimit });
  if (count === 0) return i18n.t('lib2.voiceRoom.empty');
  return i18n.t('lib2.voiceRoom.members', { count });
}

/** Best display name for a participant tile: nickname → display name →
 *  username → a short id stub when the member row is unknown. */
export function participantLabel(
  member: GuildMemberRow | null | undefined,
  userId: string,
): string {
  return member?.nickname || member?.user.displayName || member?.user.username || userId.slice(0, 6);
}
